<template>
  <div class="deploy-page admin-page">
    <header class="admin-page__header">
      <div class="admin-page__title">
        <h2>部署检测</h2>
        <p>迁服后一键体检：数据库 / Redis / 数据表，并可一键建表 + 写入配置</p>
      </div>
      <a-button :loading="checking" @click="runCheck">重新检测</a-button>
    </header>

    <!-- ① 体检面板 -->
    <a-card :bordered="false" class="admin-card" title="环境体检">
      <a-spin :spinning="checking">
        <a-row :gutter="16">
          <a-col :span="12">
            <div class="check-item">
              <a-tag :color="check.mysql.ok ? 'green' : 'red'">{{ check.mysql.ok ? '正常' : '异常' }}</a-tag>
              <span class="check-item__name">MySQL 连接</span>
              <div class="check-item__desc">{{ check.mysql.message || '—' }}</div>
              <div class="check-item__desc" v-if="check.mysql.config">
                {{ check.mysql.config.username }}@{{ check.mysql.config.host }}:{{ check.mysql.config.port }} / {{ check.mysql.config.database || '(未设库名)' }}
              </div>
            </div>
          </a-col>
          <a-col :span="12">
            <div class="check-item">
              <a-tag :color="check.redis.ok ? 'green' : 'red'">{{ check.redis.ok ? '正常' : '异常' }}</a-tag>
              <span class="check-item__name">Redis 连接</span>
              <div class="check-item__desc">{{ check.redis.message || '—' }}</div>
              <div class="check-item__desc">
                用户 {{ check.redis.user_count }} 个，其中 admin
                <a-tag :color="check.redis.admin_count > 0 ? 'green' : 'orange'" size="small">{{ check.redis.admin_count }}</a-tag>
                <span v-if="check.redis.ok && check.redis.admin_count === 0" class="warn">（无 admin，装库后可能无法登录后台）</span>
              </div>
            </div>
          </a-col>
        </a-row>

        <a-divider />

        <div class="check-item">
          <a-tag :color="check.tables.all_present ? 'green' : 'red'">
            {{ check.tables.all_present ? '齐全' : '缺失' }}
          </a-tag>
          <span class="check-item__name">数据表</span>
          <div class="check-item__desc">
            <template v-if="check.tables.present.length">
              已建：
              <a-tag v-for="t in check.tables.present" :key="'p-' + t" color="green">{{ t }}</a-tag>
            </template>
          </div>
          <div class="check-item__desc" v-if="check.tables.missing.length">
            缺失：
            <a-tag v-for="t in check.tables.missing" :key="'m-' + t" color="red">{{ t }}</a-tag>
          </div>
        </div>

        <a-divider />

        <div class="check-item">
          <a-tag :color="check.config.ok ? 'green' : 'orange'">
            {{ check.config.ok ? '已配置' : '有占位' }}
          </a-tag>
          <span class="check-item__name">配置占位（TODO）</span>
          <div class="check-item__desc" v-if="check.config.todos.length">
            待运维填写：
            <a-tag v-for="k in check.config.todos" :key="k" color="orange">{{ k }}</a-tag>
          </div>
          <div class="check-item__desc" v-else>无未填占位</div>
        </div>

        <div class="check-item" v-if="check.installed">
          <a-tag color="blue">已安装</a-tag>
          <span class="check-item__desc">检测到 config/.installed 标记（已执行过一键装库）</span>
        </div>
      </a-spin>
    </a-card>

    <!-- ② 一键装库 -->
    <a-card :bordered="false" class="admin-card" title="一键装库（填写数据库连接 → 建表 + 写入配置）">
      <a-alert
        type="info"
        show-icon
        style="margin-bottom: 16px"
        message="执行内容：测试连接 → 建库（若不存在）→ 建所有表 + 种子（套餐 / 系统设置）→ 备份并改写 config/app.php 的数据库连接。不创建管理员、不导入域名。"
      />
      <a-form layout="horizontal" :label-col="{ span: 5 }" :wrapper-col="{ span: 14 }">
        <a-form-item label="数据库主机">
          <a-input v-model="form.host" placeholder="127.0.0.1" />
        </a-form-item>
        <a-form-item label="端口">
          <a-input v-model="form.port" placeholder="3306" />
        </a-form-item>
        <a-form-item label="数据库名">
          <a-input v-model="form.database" placeholder="aegis（不存在会自动创建）" />
        </a-form-item>
        <a-form-item label="用户名">
          <a-input v-model="form.username" placeholder="aegis" />
        </a-form-item>
        <a-form-item label="密码">
          <a-input-password v-model="form.password" placeholder="数据库密码" />
        </a-form-item>
        <a-form-item :wrapper-col="{ span: 14, offset: 5 }">
          <a-popconfirm
            title="确认用以上连接建表并改写服务器配置？原 app.php 会自动备份。"
            ok-text="确认执行"
            cancel-text="取消"
            @confirm="runInstall"
          >
            <a-button type="primary" :loading="installing">测试并安装</a-button>
          </a-popconfirm>
          <span class="form-hint">建议执行前先「重新检测」确认 MySQL 连接信息</span>
        </a-form-item>
      </a-form>

      <!-- 执行结果 -->
      <div v-if="steps.length" class="install-result">
        <a-divider>执行结果</a-divider>
        <a-list size="small" :data-source="steps">
          <a-list-item slot="renderItem" slot-scope="item">
            <a-tag :color="item.ok ? 'green' : 'red'">{{ item.ok ? 'OK' : 'FAIL' }}</a-tag>
            <strong style="margin: 0 8px">{{ item.name }}</strong>
            <span class="check-item__desc">{{ item.message }}</span>
          </a-list-item>
        </a-list>
      </div>
    </a-card>
  </div>
</template>

<script>
import { deployCheck, deployInstall } from '@/api/deploy';

function emptyCheck() {
  return {
    mysql: { ok: false, message: '', config: null },
    tables: { ok: false, present: [], missing: [], all_present: false },
    redis: { ok: false, message: '', user_count: 0, admin_count: 0 },
    config: { ok: true, todos: [] },
    installed: false,
  };
}

export default {
  data() {
    return {
      checking: false,
      installing: false,
      check: emptyCheck(),
      form: { host: '127.0.0.1', port: '3306', database: 'aegis', username: 'aegis', password: '' },
      steps: [],
    };
  },
  mounted() {
    this.runCheck();
  },
  methods: {
    runCheck() {
      this.checking = true;
      deployCheck()
        .then(res => {
          const d = res.data || {};
          this.check = Object.assign(emptyCheck(), d);
          // 用现有配置预填表单（密码不回传，留空）
          if (d.mysql && d.mysql.config) {
            this.form.host = d.mysql.config.host || this.form.host;
            this.form.port = String(d.mysql.config.port || this.form.port);
            this.form.database = d.mysql.config.database || this.form.database;
            this.form.username = d.mysql.config.username || this.form.username;
          }
        })
        .catch(e => this.$message.error((e && e.data && e.data.message) || '检测失败'))
        .finally(() => (this.checking = false));
    },
    runInstall() {
      this.installing = true;
      this.steps = [];
      deployInstall({
        host: this.form.host,
        port: Number(this.form.port) || 3306,
        database: this.form.database,
        username: this.form.username,
        password: this.form.password,
      })
        .then(res => {
          const d = res.data || {};
          this.steps = d.steps || [];
          if (d.ok) {
            this.$message.success(res.message || '部署完成');
          } else {
            this.$message.error(res.message || '安装未完成，请查看执行结果');
          }
          this.runCheck();
        })
        .catch(e => this.$message.error((e && e.data && e.data.message) || '安装请求失败'))
        .finally(() => (this.installing = false));
    },
  },
};
</script>

<style scoped>
.deploy-page .admin-card {
  margin-bottom: 24px;
}
.check-item {
  margin-bottom: 8px;
}
.check-item__name {
  font-weight: 600;
  margin-left: 4px;
}
.check-item__desc {
  color: #8c8c8c;
  margin: 4px 0 4px 2px;
  word-break: break-all;
}
.check-item__desc .ant-tag {
  margin-bottom: 4px;
}
.warn {
  color: #fa8c16;
}
.form-hint {
  margin-left: 12px;
  color: #8c8c8c;
}
.install-result {
  margin-top: 8px;
}
</style>
